import { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "../components/Layout";
import api, { formatApiError } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { ChevronRight, CheckCircle2 } from "lucide-react";

const RATER_HERO = "https://images.unsplash.com/photo-1762504634376-740d95eec2e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBjb3Jwb3JhdGUlMjBwcm9mZXNzaW9uYWxzJTIwd29ya2luZyUyMGluJTIwb2ZmaWNlfGVufDB8fHx8MTc3OTA5ODIyN3ww&ixlib=rb-4.1.0&q=85";

export default function RaterApp() {
  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-[1440px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <Routes>
          <Route index element={<Tasks />} />
          <Route path="rate/:assignmentId" element={<Rate />} />
        </Routes>
      </main>
    </div>
  );
}

function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  useEffect(() => { api.get("/rater/tasks").then((r) => setTasks(r.data)); }, []);
  if (!tasks) return <div className="text-center text-[#878787] py-20">Loading…</div>;

  const pending = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="space-y-10 fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        <div className="lg:col-span-2">
          <div className="overline mb-2">{user.role === "manager" ? "MANAGER 360° RATER" : "CHANNEL PARTNER 360° RATER"}</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Your rating tasks</h1>
          <p className="text-[#525252] text-sm mt-2 max-w-2xl">
            Rate each team member you work with across the 7 competencies. Use the 1–5 BARS scale. Add a short comment per competency — the more specific, the more useful for the participant's IDP.
          </p>
        </div>
        <div className="card overflow-hidden h-44 lg:h-auto">
          <img src={RATER_HERO} alt="" className="w-full h-full object-cover" />
        </div>
      </div>

      <section>
        <h3 className="font-heading font-bold text-lg mb-4">Pending · {pending.length}</h3>
        {pending.length === 0 && <div className="card p-6 text-[#878787] text-sm">All caught up. 🎯</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pending.map((t) => (
            <TaskCard key={t.id} t={t} />
          ))}
        </div>
      </section>

      {done.length > 0 && (
        <section>
          <h3 className="font-heading font-bold text-lg mb-4">Completed · {done.length}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {done.map((t) => (
              <div key={t.id} className="card p-5 flex justify-between items-center opacity-70">
                <div>
                  <div className="font-semibold text-sm">{t.participant_name}</div>
                  <div className="text-xs text-[#878787]">{t.cycle?.name}</div>
                </div>
                <span className="badge badge-success"><CheckCircle2 size={11} /> Done</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TaskCard({ t }) {
  return (
    <div className="card p-5 flex justify-between items-center" data-testid={`rate-card-${t.id}`}>
      <div>
        <div className="font-heading font-bold text-base">{t.participant_name}</div>
        <div className="text-xs text-[#525252] mt-1">
          <span className="badge badge-red mr-2">{t.participant_role_level}</span>
          {t.cycle?.name}
        </div>
      </div>
      <a href={`/rater/rate/${t.id}`} className="btn btn-primary !py-2 !px-4 !text-xs" data-testid={`rate-btn-${t.id}`}>
        Rate <ChevronRight size={14} />
      </a>
    </div>
  );
}

function Rate() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [meta, setMeta] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/rater/tasks").then((r) => {
      const a = r.data.find((x) => x.id === assignmentId);
      setAssignment(a);
    });
    api.get("/assessment/questions").then((r) => setMeta(r.data));
  }, [assignmentId]);

  if (!assignment || !meta) return <div className="text-center text-[#878787] py-20">Loading…</div>;

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      await api.post("/rater/submit", { assignment_id: assignmentId, ratings, comments });
      navigate("/rater");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto fade-in">
      <button onClick={() => navigate("/rater")} className="btn btn-ghost !px-2 mb-4">← Back</button>
      <div className="overline mb-2">RATING · {assignment.participant_name.toUpperCase()}</div>
      <h2 className="font-heading font-black text-3xl tracking-tight mb-2">Rate {assignment.participant_name}</h2>
      <p className="text-[#525252] text-sm mb-10">
        Rate each competency on a 1–5 BARS scale, then add one specific observation per competency.
      </p>

      <div className="space-y-8">
        {meta.competencies.map((c) => (
          <div key={c.key} className="card p-6">
            <div className="overline mb-1">{c.type}</div>
            <h4 className="font-heading font-bold text-lg mb-3">{c.name}</h4>
            <div className="flex mb-4">
              {[1,2,3,4,5].map((lvl) => (
                <button key={lvl} type="button"
                  data-testid={`rate-${c.key}-${lvl}`}
                  onClick={() => setRatings({ ...ratings, [c.key]: lvl })}
                  className={`bars-segment ${ratings[c.key] === lvl ? "active" : ""}`}>
                  <div className="font-heading font-black">{lvl}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-0.5">{meta.proficiency_labels[lvl]}</div>
                </button>
              ))}
            </div>
            <textarea
              rows={2} className="input font-body" placeholder="One specific example or observation..."
              value={comments[c.key] || ""} onChange={(e) => setComments({ ...comments, [c.key]: e.target.value })}
              data-testid={`comment-${c.key}`}
            />
          </div>
        ))}
      </div>

      {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 mt-6 rounded-sm">{err}</div>}

      <div className="flex justify-end mt-8 pt-6 border-t border-[#E5E7EB]">
        <button onClick={submit} disabled={submitting} className="btn btn-primary" data-testid="rater-submit">
          {submitting ? "Submitting…" : <>Submit feedback <CheckCircle2 size={16} /></>}
        </button>
      </div>
    </div>
  );
}
